import { ClosedNumericRange, NonemptyClosedNumericRange } from 'utils/range/ranges';
import { filter, forEach, map } from 'es-toolkit/compat';

/**
 * Single map entry in ClosedIntegerRangeMap below.
 */
export interface Entry<T> {
  range: NonemptyClosedNumericRange;
  value: T;
}

/**
 * Mapping from ranges of party sizes to a TurnTimes. Similar to Guava's
 * RangeMap in Java.
 */
export default class ClosedIntegerRangeMap<T> {
  entries: Array<Entry<T>>;

  constructor(entries: Array<Entry<T>>) {
    this.entries = filter(entries, (entries) => entries.value !== undefined);
  }

  /**
   * Returns a sub-map whose range is restricted to the given range.
   */
  restrictToRange(range: ClosedNumericRange): ClosedIntegerRangeMap<T> {
    const newEntries: Array<Entry<T>> = [];
    forEach(this.entries, (entry) => {
      const intersectedRange = entry.range.intersect(range).asNonemptyRange();
      if (intersectedRange) {
        newEntries.push({
          range: intersectedRange,
          value: entry.value,
        });
      }
    });
    return new ClosedIntegerRangeMap<T>(newEntries);
  }

  /**
   * Gets the value associated with the given number in this map.
   */
  get(n: number): T | undefined {
    for (let i = 0; i < this.entries.length; i++) {
      if (this.entries[i].range.includes(n)) {
        return this.entries[i].value;
      }
    }
    return undefined;
  }

  /**
   * Returns all values in this map. The same value could be returned multiple
   * times if it is mapped multiple times.
   */
  values(): T[] {
    return map(this.entries, (entry) => entry.value);
  }
}
